/**
 * MergeMap caches a mapping between remote items and their local projections,
 * preserving local-only fields while syncing with new remote data.
 *
 * Ported (with minor naming adjustments) from React Flow's collaborative-pro
 * example (`src/lib/mergeMap.ts`). The pattern: each entry holds a {remote,
 * local} pair where `local` extends `remote` with fields React Flow manages
 * but Yjs doesn't (selected, measured). On every Yjs snapshot, `merge(items)`
 * walks the new remote list, returns the cached local for unchanged items
 * (stable reference → no re-render), and re-derives merging old local +
 * new remote when remote actually changed.
 *
 * Why this beats a useState/useRef merge in CanvasEngine: the previous
 * implementation tracked the previous Yjs snapshot via a useRef, then
 * compared yNode references to the ref inside a setLocalNodes closure. When
 * two yjsNodes updates landed before React committed the first setState,
 * the ref was overwritten before the first state-setter closure ran. The
 * race mis-classified freshly-arrived remote nodes as "unchanged" and
 * returned the (undefined) local — dropping nodes from the rendered set.
 * MergeMap holds the cache outside React, so two consecutive merges are
 * just two consecutive function calls, no race.
 */

type MergeEntry<Remote, Local> = {
  remote: Remote;
  local: Local;
};

type GetId<Remote> = (item: Remote) => string;
type DeriveLocal<Remote, Local> = (item: Remote) => Partial<Local>;
type IsRemoteEqual<Remote> = (a: Remote, b: Remote) => boolean;

export class MergeMap<Remote, Local> {
  private readonly entriesById: Map<string, MergeEntry<Remote, Local>>;
  private readonly getId: GetId<Remote>;
  private readonly deriveLocal: DeriveLocal<Remote, Local>;
  private readonly isRemoteEqual: IsRemoteEqual<Remote>;

  constructor(options: {
    getId: GetId<Remote>;
    deriveLocal: DeriveLocal<Remote, Local>;
    /** Defaults to reference equality, which is what RF's example uses. */
    isRemoteEqual?: IsRemoteEqual<Remote>;
  }) {
    this.getId = options.getId;
    this.deriveLocal = options.deriveLocal;
    this.isRemoteEqual = options.isRemoteEqual ?? ((a, b) => a === b);
    this.entriesById = new Map<string, MergeEntry<Remote, Local>>();
  }

  /**
   * Merge the cache with the latest remote items and return the derived
   * locals in input order. Items missing from the new remote list are
   * pruned. Items with stable remote references reuse the cached local
   * unchanged (stable identity → React Flow can skip re-render).
   */
  merge(remoteItems: readonly Remote[]): Local[] {
    const unseen = new Map(this.entriesById);

    const locals: Local[] = remoteItems.map((remote) => {
      const id = this.getId(remote);
      unseen.delete(id);

      const cached = this.entriesById.get(id);
      if (cached && this.isRemoteEqual(cached.remote, remote)) {
        return cached.local;
      }

      const updatedLocal: Local = {
        ...cached?.local,
        ...this.deriveLocal(remote),
      } as Local;

      this.entriesById.set(id, { remote, local: updatedLocal });
      return updatedLocal;
    });

    unseen.forEach((_, id) => {
      this.entriesById.delete(id);
    });

    return locals;
  }

  get(id: string): MergeEntry<Remote, Local> | undefined {
    return this.entriesById.get(id);
  }

  /** Replace the cache entry for `id` (used for local-only mutations: select, dimensions). */
  set(id: string, remote: Remote, local: Local): MergeEntry<Remote, Local> {
    const entry = { remote, local };
    this.entriesById.set(id, entry);
    return entry;
  }

  delete(id: string): boolean {
    return this.entriesById.delete(id);
  }

  /** Snapshot all locals in iteration order (insertion order). Used by callers that need
   *  to push the cached locals into a downstream consumer (e.g. React state) without
   *  re-running merge. */
  values(): Local[] {
    return Array.from(this.entriesById.values()).map((e) => e.local);
  }
}
